import { useState } from 'react';
import { motion } from 'motion/react';
import { LegProgressCard } from './leg-progress-card';
import { RouteJourney3D } from './route-journey-3d';
import { importTrackingService } from '../services/import-tracking-service';
import type { ImportOrderLeg, ImportOrderStatus } from '../types/import-tracking.types';
import { motivoParaNoMoverTramos, motivoParaNoArrancarTramo } from '../utils/order-state-rules';
import { cn } from '@/lib/utils/cn';
import { useToast } from '@/lib/hooks/use-toast';

interface MultimodalJourneyProps {
  orderId: string;
  legs: ImportOrderLeg[];
  /**
   * El estado de la orden dueña de estos tramos.
   *
   * Hace falta para saber si los tramos se pueden mover: sin la orden
   * confirmada no se mueve carga. Es opcional para no romper a quien dibuje el
   * recorrido fuera de una orden (el portal público, que además es de solo
   * lectura); si no viene, no se bloquea nada y manda el backend.
   */
  orderStatus?: ImportOrderStatus;
  isReadonly?: boolean;
  onEditLeg?: (legId: string) => void;
  onDeleteLeg?: (legId: string) => void;
  onAddLeg?: () => void;
  onRefresh?: () => void;
  className?: string;
}

export function MultimodalJourney({
  orderId: _orderId,
  legs,
  orderStatus,
  isReadonly,
  onEditLeg,
  onDeleteLeg,
  onAddLeg,
  onRefresh,
  className,
}: MultimodalJourneyProps) {
  const { toast } = useToast();
  const [advancingId, setAdvancingId] = useState<string | null>(null);

  // Se calcula una vez y se le pasa a las seis tarjetas: la razón es de la
  // orden, no de cada tramo.
  const motivoBloqueo = orderStatus ? motivoParaNoMoverTramos(orderStatus) : null;

  async function handleAdvance(legId: string) {
    setAdvancingId(legId);
    try {
      await importTrackingService.advanceLeg(legId);
      toast({ title: 'Tramo actualizado' });
      onRefresh?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error desconocido';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setAdvancingId(null);
    }
  }

  if (legs.length === 0) {
    return (
      <div className={cn('rounded-xl border-2 border-dashed p-8 text-center', className)}>
        <p className="text-sm text-muted-foreground">
          Esta orden no tiene tramos configurados. Asigna una <strong>Ruta</strong> para generarlos
          automáticamente.
        </p>
      </div>
    );
  }

  const completedCount = legs.filter((l) => l.status === 'COMPLETED').length;
  const overallPct = Math.round((completedCount / legs.length) * 100);

  /*
    El total y los días, arriba del todo.

    Antes había que leer las tarjetas una por una y sumar de memoria para saber
    cuánto sale el flete. Es el número que primero busca cualquiera que abre la
    pantalla.
  */
  const totalFlete = legs.reduce((s, l) => s + Number(l.calculatedAmount ?? 0), 0);
  const moneda = legs[0]?.currency ?? 'USD';

  return (
    <div className={cn('space-y-3', className)}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex flex-wrap items-center justify-between gap-3 px-1"
      >
        <div className="flex items-baseline gap-3">
          <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
            Ruta · {legs.length} tramo{legs.length !== 1 ? 's' : ''}
          </h3>
          {totalFlete > 0 && (
            <span className="text-sm font-bold tabular-nums text-ok">
              {moneda}{' '}
              {totalFlete.toLocaleString('es-BO', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="h-2 w-32 overflow-hidden rounded-full bg-muted">
            <motion.div
              className="h-full bg-gradient-to-r from-primary to-emerald-500"
              initial={{ width: 0 }}
              animate={{ width: `${overallPct}%` }}
              transition={{ duration: 0.8 }}
            />
          </div>
          <span className="text-xs font-bold tabular-nums">{overallPct}%</span>
        </div>
      </motion.div>

      {/*
        El viaje en 3D. Va arriba de las tarjetas y no en lugar de ellas: la
        escena se lee de un vistazo —dónde está la carga, cuál es el tramo
        largo— y las tarjetas siguen teniendo las fechas, el proveedor y los
        botones, que es donde se trabaja.
      */}
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="overflow-hidden rounded-xl border bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950"
      >
        <RouteJourney3D legs={legs} />
      </motion.div>

      <div className="grid gap-3">
        {legs.map((leg, idx) => (
          <motion.div
            key={leg.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: idx * 0.08 }}
          >
            <LegProgressCard
              leg={leg}
              isReadonly={isReadonly || advancingId !== null}
              // El motivo de la orden manda sobre el de la cadena: si no está
              // confirmada, no tiene sentido decir "falta el tramo 1".
              motivoBloqueo={motivoBloqueo ?? motivoParaNoArrancarTramo(leg, legs)}
              onAdvance={handleAdvance}
              onEdit={onEditLeg}
              onDelete={onDeleteLeg}
              onPricingUpdated={onRefresh}
            />
          </motion.div>
        ))}
        {!isReadonly && onAddLeg && (
          <button
            type="button"
            onClick={onAddLeg}
            className="w-full rounded-xl border-2 border-dashed border-border p-4 text-sm font-medium text-muted-foreground hover:border-primary hover:text-primary transition-colors"
          >
            + Agregar tramo
          </button>
        )}
      </div>
    </div>
  );
}
